import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Tiers stack: reaching one keeps every perk of the tiers below it, so a higher tier may
 * add only a discount, only free shipping, or both.
 *
 * @typedef {object} BundleTier
 * @property {number} quantity - Items required to reach the tier.
 * @property {number} discount - Percentage off.
 * @property {boolean} free_shipping - Whether the tier includes free shipping.
 */

/**
 * @typedef {object} BarParts
 * @property {HTMLElement | null} fill - The progress bar fill.
 * @property {HTMLElement | null} message - The progress message.
 */

/**
 * A bar in the cart drawer tracking how close the cart is to the next quantity reward for one
 * or more product types, counted together. It stays hidden until a matching item is in the cart.
 *
 * The cart is read from `/cart.js` rather than rendered in Liquid, because this store caches
 * HTML at the edge and a cached page must not carry one visitor's cart. That endpoint is
 * never cached, and `product_type` comes back on every line, so the whole rule lives here.
 *
 * @extends Component
 */
class BundleProgressComponent extends Component {
  /** @type {BundleTier[]} */
  #tiers = [];

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {BarParts} */
  #parts = { fill: null, message: null };

  #count = 0;

  /** Highest tier reached at the last render, so a *newly* reached one can be celebrated. */
  #reachedQuantity = 0;

  /** First render seeds the baseline: arriving with a qualifying cart is not an unlock. */
  #seeded = false;

  /** @type {number | undefined} */
  #celebrateTimeout;

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();

    this.#tiers = this.#parse(this.dataset.tiers)
      .filter((/** @type {BundleTier} */ tier) => tier.quantity > 0)
      .sort((/** @type {BundleTier} */ a, /** @type {BundleTier} */ b) => a.quantity - b.quantity);

    this.#cacheParts();

    this.#refresh();
    document.addEventListener(ThemeEvents.cartUpdate, this.#refresh);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#refresh);
    this.#activeFetch?.abort();
    clearTimeout(this.#celebrateTimeout);
  }

  #cacheParts() {
    this.#bar = this.querySelector('[data-bundle-bar]');
    if (!this.#bar) return;

    this.#parts = {
      fill: this.#bar.querySelector('[data-role="fill"]'),
      message: this.#bar.querySelector('[data-role="message"]'),
    };
  }

  /** Reads the cart and re-renders. Also the `cart:update` handler, so it is a field. */
  #refresh = async () => {
    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        signal: abortController.signal,
      });

      const cart = await response.json();
      const productTypes = new Set(
        (this.dataset.productType ?? '')
          .split(',')
          .map((type) => type.trim())
          .filter(Boolean)
      );

      let count = 0;

      for (const item of cart.items ?? []) {
        if (productTypes.has(item.product_type)) count += item.quantity;
      }

      this.#count = count;
      this.#render();
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  };

  #render() {
    const { fill, message } = this.#parts;
    const count = this.#count;
    const goal = this.#tiers.at(-1)?.quantity ?? 0;

    if (fill) fill.style.width = `${goal > 0 ? Math.min(count / goal, 1) * 100 : 0}%`;

    if (message) message.textContent = this.#messageFor(count);

    this.#updateUnlockState(this.#currentTier);

    if (this.#bar) this.#bar.hidden = this.#tiers.length === 0 || count === 0;
  }

  /**
   * One line for three states: below the first tier, between tiers, and done.
   * @param {number} count - Qualifying items in the cart.
   * @returns {string} The progress message.
   */
  #messageFor(count) {
    const current = this.#currentTier;
    const next = this.#tiers.find((tier) => count < tier.quantity);

    if (!next) return current ? `\u{1F389} ${this.#rewardFor(current)}!` : '';

    return this.#text('textAddMore', { count: next.quantity - count, reward: this.#rewardFor(next, current) });
  }

  /**
   * @param {BundleTier} tier - The tier to describe.
   * @param {BundleTier} [earned] - The perks already held, which are not worth re-offering.
   * @returns {string} e.g. "15% Off and Free Shipping".
   */
  #rewardFor(tier, earned) {
    const parts = [];
    if (tier.discount > (earned?.discount ?? 0)) parts.push(this.#text('textPercentOff', { percent: tier.discount }));
    if (tier.free_shipping && !earned?.free_shipping) parts.push(this.#text('textFreeShipping'));
    return parts.join(` ${this.#text('textAnd')} `);
  }

  /**
   * Fills a translated template from Liquid, whose placeholders are rendered as `[name]`.
   * @param {string} key - The dataset key holding the template.
   * @param {Record<string, string | number>} [values] - The placeholder values.
   * @returns {string} The filled-in text.
   */
  #text(key, values = {}) {
    return (this.dataset[key] ?? '').replace(/\[(\w+)\]/g, (match, name) => String(values[name] ?? match));
  }

  /**
   * The perks the cart currently holds: the highest reached tier's quantity, with the perks of
   * every reached tier folded in, since tiers stack.
   *
   * @returns {BundleTier | undefined} Undefined when no tier is reached.
   */
  get #currentTier() {
    const reached = this.#tiers.filter((tier) => this.#count >= tier.quantity);
    if (reached.length === 0) return undefined;

    return reached.reduce((earned, tier) => ({
      quantity: tier.quantity,
      discount: Math.max(earned.discount, tier.discount),
      free_shipping: earned.free_shipping || tier.free_shipping,
    }));
  }

  /**
   * Marks the transition into a new tier so the status line can react to it. Landing on the
   * page already qualifying is not a transition, so the first render only seeds the baseline.
   *
   * @param {BundleTier | undefined} tier - The tier currently reached.
   */
  #updateUnlockState(tier) {
    if (!this.#bar) return;

    const reached = tier?.quantity ?? 0;
    const isUnlock = this.#seeded && reached > this.#reachedQuantity;

    this.#reachedQuantity = reached;
    this.#seeded = true;

    if (!isUnlock) return;

    clearTimeout(this.#celebrateTimeout);
    this.#bar.dataset.celebrate = 'true';

    this.#celebrateTimeout = setTimeout(() => {
      this.#bar?.removeAttribute('data-celebrate');
    }, 600);
  }

  /**
   * @param {string | undefined} value - The JSON string to parse.
   * @returns {any[]} The parsed array, empty when parsing fails.
   */
  #parse(value) {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}

if (!customElements.get('bundle-progress-component')) {
  customElements.define('bundle-progress-component', BundleProgressComponent);
}
